import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AddressAutocomplete from '../components/AddressAutocomplete';

export default function UserProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [creditBalances, setCreditBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    pid: null,
    streetNumber: '',
    streetName: '',
    streetType: '',
    coordinates: [0, 0],
    farmName: '',
    tagline: '',
    aboutText: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchCreditBalances();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setProfile(data);
setFormData({
  name: data.name || '',
  phone: data.phone || '',
  address: data.location?.address || '',
  suburb: data.location?.suburb || '',
  state: data.location?.state || '',
  postcode: data.location?.postcode || '',
  pid: data.location?.pid || null,
  streetNumber: data.location?.streetNumber || '',
  streetName: data.location?.streetName || '',
  streetType: data.location?.streetType || '',
  coordinates: data.location?.coordinates || [0, 0],
  farmName: data.farmName || '',
  tagline: data.tagline || '',
  aboutText: data.aboutText || ''
});
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditBalances = async () => {
    try {
      const res = await fetch('/api/credits/balances', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setCreditBalances(data);
    } catch (err) {
      console.error('Failed to fetch credit balances:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      setSuccess('Profile updated successfully!');
      setProfile(data.user);
      setIsEditing(false);
      await fetchProfile();
    } catch (err) {
      console.error('Update error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading profile...</div>;
  if (error && !profile) return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  if (!profile) return <div className="text-center py-8">Profile not found</div>;

  const totalCredits = creditBalances.reduce((sum, b) => sum + b.balance, 0);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Your Profile</h1>
          {!isEditing && (
            <button onClick={() => setIsEditing(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              Edit Profile
            </button>
          )}
        </div>

        {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}
        {success && <div className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</div>}

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 mb-1">Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full border rounded-md px-3 py-2" />
            </div>

            <div>
              <label className="block text-gray-700 mb-1">Phone</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full border rounded-md px-3 py-2" />
            </div>

            {profile.role === 'producer' && (
              <div>
                <label className="block text-gray-700 mb-1">Farm Name</label>
                <input type="text" name="farmName" value={formData.farmName} onChange={handleChange} className="w-full border rounded-md px-3 py-2" />
              </div>
            )}

            {(profile.role === 'producer' || profile.role === 'hub') && (
              <>
                <div>
                  <label className="block text-gray-700 mb-1">Tagline</label>
                  <input type="text" name="tagline" value={formData.tagline} onChange={handleChange} className="w-full border rounded-md px-3 py-2" placeholder="e.g., Fresh from our farm to your table" />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">About</label>
                  <textarea name="aboutText" value={formData.aboutText} onChange={handleChange} rows="3" className="w-full border rounded-md px-3 py-2" placeholder="Tell customers about your story" />
                </div>
              </>
            )}

<div className="border-t pt-4 mt-4">
  <h3 className="font-semibold mb-3">Address (for delivery)</h3>
  <AddressAutocomplete
    value={formData.address}
    onChange={(text) => setFormData(prev => ({ ...prev, address: text }))}
    onSelect={(result) => setFormData(prev => ({
      ...prev,
      address: result.address,
      suburb: result.components.suburb,
      state: result.components.state,
      postcode: result.components.postcode,
      pid: result.pid,
      streetNumber: result.components.number,
      streetName: result.components.streetName,
      streetType: result.components.streetType,
      coordinates: result.coordinates
    }))}
    placeholder="Start typing your address..."
  />
  {formData.suburb && (
    <p className="text-xs text-gray-500 mt-1">
      ✓ {formData.suburb} {formData.state} {formData.postcode}
      {formData.pid && <span className="ml-2 text-gray-400">({formData.pid})</span>}
    </p>
  )}
</div>

            <div className="flex space-x-3 pt-4">
              <button type="submit" disabled={saving} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={() => setIsEditing(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <p><strong>Name:</strong> {profile.name}</p>
            <p><strong>Email:</strong> {profile.email}</p>
            <p><strong>Role:</strong> {profile.role}</p>
            {profile.phone && <p><strong>Phone:</strong> {profile.phone}</p>}
            {profile.role === 'producer' && profile.farmName && <p><strong>Farm Name:</strong> {profile.farmName}</p>}
            {(profile.role === 'producer' || profile.role === 'hub') && profile.tagline && <p><strong>Tagline:</strong> {profile.tagline}</p>}
            {(profile.role === 'producer' || profile.role === 'hub') && profile.aboutText && <p><strong>About:</strong> {profile.aboutText}</p>}
            {profile.location?.address && (
              <p><strong>Address:</strong> {profile.location.address}, {profile.location.suburb} {profile.location.state} {profile.location.postcode}</p>
            )}

            {/* Unified Credit Display */}
            <div className="mt-4 pt-4 border-t">
              <h3 className="font-semibold mb-2">Credits</h3>
              {creditBalances.length === 0 ? (
                <p className="text-gray-500 text-sm">No credits available</p>
              ) : (
                <div className="space-y-2">
                  <div className="bg-green-50 p-2 rounded">
                    <span className="font-bold">Total: {totalCredits} credits</span>
                  </div>
                  {creditBalances.map((balance, idx) => (
                    <div key={idx} className="flex justify-between text-sm border-b py-1">
                      <span>{balance.issuerName}:</span>
                      <span className="font-semibold text-green-600">{balance.balance} credits</span>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 mt-2">Credits can only be used with the issuer who awarded them.</p>
                </div>
              )}
            </div>

            {profile.role === 'pickup' && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="font-semibold">Pickup Stats</h3>
                <p>Completed Deliveries: {profile.pickupSettings?.completedDeliveries || 0}</p>
                <p>Rating: {profile.pickupSettings?.rating || 5.0} ★</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
